// Super-admin support console — "why doesn't this parent see her data?"
//
// One-stop diagnostic: paste a parent email, see every user record for
// that email, per-id counts of PFP + Clinical children, and active
// therapist_parent_links. Built specifically to unblock the recurring
// Jayde-shaped bug where a dual-role parent's PFP data lives under a
// parent_self twin id.
import { useState } from 'react';
import axios from 'axios';
import { Search, Copy, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminParentDataLookup() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const lookup = async () => {
    const trimmed = (email || '').trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/admin/support/parent-lookup`,
        { params: { email: trimmed }, headers: { Authorization: `Bearer ${token}` } },
      );
      setResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Lookup failed');
      toast.error('Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const copyJson = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
      toast.success('JSON copied — paste it back to me');
    } catch {
      toast.error('Copy failed — you can select the text manually');
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="admin-parent-lookup-page">
      <div>
        <h1 className="text-2xl font-serif text-stone-900">Parent data lookup</h1>
        <p className="text-sm text-stone-600 mt-1">
          Diagnose "I can't see my children" by seeing every trace of a parent's data
          across the DB. Type an email, press Look up, then click Copy JSON to share
          the full result.
        </p>
      </div>

      <Card className="p-4">
        <div className="flex gap-2 items-center">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && lookup()}
            placeholder="jayde@hatfieldgroup.co.za"
            className="flex-1"
            data-testid="admin-parent-lookup-email"
          />
          <Button
            onClick={lookup}
            disabled={loading}
            data-testid="admin-parent-lookup-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-2">Look up</span>
          </Button>
        </div>
        {error && (
          <div className="mt-3 flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </Card>

      {result && (
        <>
          {/* Summary cards — the highest-signal answer */}
          <Card className="p-4 space-y-3" data-testid="admin-parent-lookup-summary">
            <h2 className="font-serif text-lg text-stone-900">
              Records for {result.email}
            </h2>
            {result.user_records?.length === 0 ? (
              <p className="text-sm text-rose-700">No user record found for that email.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {result.user_records.map((u) => (
                  <div
                    key={u.id}
                    className="border border-stone-200 rounded-md p-3 text-sm space-y-1"
                    data-testid={`admin-parent-lookup-record-${u.role}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-wider font-medium text-[#7C6F9C]">
                        {u.role}
                      </span>
                      <code className="text-[10px] text-stone-500 truncate">{u.id}</code>
                    </div>
                    <div className="text-xs text-stone-600 space-y-0.5">
                      <div>has_parent_app_access: <b>{String(u.has_parent_app_access)}</b></div>
                      <div>parent_subscription_status: <b>{u.parent_subscription_status || '—'}</b></div>
                      <div>sponsoring_practice_id: <b>{u.sponsoring_practice_id || '—'}</b></div>
                    </div>
                    <div className="pt-2 mt-2 border-t border-stone-100 text-xs">
                      <div>PFP children: <b>{u.counts.parent_children}</b></div>
                      <div>Clinical children: <b>{u.counts.children_clinical}</b></div>
                      <div>Language maps: <b>{u.counts.parent_language_maps}</b></div>
                      <div>Feedback rows: <b>{u.counts.parent_activity_feedback}</b></div>
                      <div>Play ideas: <b>{u.counts.parent_play_ideas}</b></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="pt-2 text-xs text-stone-600">
              Active therapist_parent_links: <b>{result.therapist_parent_links?.length || 0}</b>
            </div>
          </Card>

          {/* Full JSON + copy button — for pasting back to me */}
          <Card className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-stone-900">Full JSON</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={copyJson}
                data-testid="admin-parent-lookup-copy-btn"
              >
                <Copy className="w-4 h-4 mr-1" /> Copy JSON
              </Button>
            </div>
            <pre
              className="text-xs bg-stone-50 border border-stone-200 rounded-md p-3 overflow-auto max-h-96"
              data-testid="admin-parent-lookup-json"
            >
              {JSON.stringify(result, null, 2)}
            </pre>
          </Card>
        </>
      )}
    </div>
  );
}
